import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Trash, Lock } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VoiceInput from "@/components/VoiceInput";
import AuthedImage from "@/components/AuthedImage";
import { toast } from "sonner";

export default function AddItemModal({ open, onClose, onCreated, initial = null }) {
  const { user } = useAuth();
  const photosLocked = !user || user.tier === "free";
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [locationId, setLocationId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [photoPath, setPhotoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [l, c] = await Promise.all([api.get("/locations"), api.get("/categories")]);
      setLocations(l.data); setCategories(c.data);
    })();
    if (initial) {
      setName(initial.name || ""); setDescription(initial.description || "");
      setQuantity(initial.quantity || 1); setLocationId(initial.location_id || "");
      setCategoryId(initial.category_id || ""); setTags(initial.tags || []);
      setPhotoPath(initial.photo_path || null);
    } else {
      setName(""); setDescription(""); setQuantity(1);
      setLocationId(""); setCategoryId(""); setTags([]); setPhotoPath(null); setTagInput("");
    }
  }, [open, initial]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const addTag = () => {
    const v = tagInput.trim();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setTagInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(), description, quantity: Number(quantity) || 1,
        location_id: locationId || null, category_id: categoryId || null,
        tags, photo_path: photoPath,
      };
      if (initial) {
        await api.patch(`/items/${initial.id}`, body);
        toast.success("Item updated");
      } else {
        await api.post("/items", body);
        toast.success("Item added");
      }
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-stoneink-900/40 backdrop-blur-md p-4 sm:p-8 flex items-start sm:items-center justify-center overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
            className="relative w-full max-w-2xl bg-white rounded-3xl shadow-hover border border-sand-300 p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="add-item-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-stoneink-900">
                {initial ? "Edit item" : "Add an item"}
              </h2>
              <button onClick={onClose} data-testid="modal-close" className="h-10 w-10 grid place-items-center rounded-xl hover:bg-sand-200">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Name</label>
                <div className="mt-2 flex gap-2">
                  <input
                    data-testid="item-name-input"
                    required value={name} onChange={(e) => setName(e.target.value)}
                    className="flex-1 h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                    placeholder="What did you just store?"
                  />
                  <VoiceInput onTranscript={(t) => setName((n) => (n ? n + " " : "") + t)} testid="voice-name-btn" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Notes</label>
                <div className="mt-2 flex gap-2">
                  <textarea
                    data-testid="item-description-input"
                    rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none resize-none"
                    placeholder="Optional details — e.g. holiday lights, warm white"
                  />
                  <VoiceInput onTranscript={(t) => setDescription((d) => (d ? d + " " : "") + t)} testid="voice-desc-btn" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Quantity</label>
                  <input
                    data-testid="item-quantity-input"
                    type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)}
                    className="mt-2 w-full h-11 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Location</label>
                  <select
                    data-testid="item-location-select"
                    value={locationId} onChange={(e) => setLocationId(e.target.value)}
                    className="mt-2 w-full h-11 px-3 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
                  >
                    <option value="">— none —</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Category</label>
                  <select
                    data-testid="item-category-select"
                    value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                    className="mt-2 w-full h-11 px-3 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 outline-none"
                  >
                    <option value="">— none —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Tags</label>
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  {tags.map((t) => (
                    <span key={t} className="px-3 py-1 rounded-full bg-sand-200 text-stoneink-700 text-sm flex items-center gap-1.5">
                      {t}
                      <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:text-terracotta-600">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <input
                    data-testid="item-tag-input"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    onBlur={addTag}
                    className="h-9 px-3 rounded-full bg-white border border-sand-300 focus:border-terracotta-500 outline-none text-sm flex-1 min-w-[140px]"
                    placeholder="Type & press enter"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Photo</label>
                <div className="mt-2 flex items-start gap-4">
                  {photosLocked ? (
                    <Link to="/pricing" data-testid="photo-locked"
                      className="h-24 w-24 rounded-2xl border-2 border-dashed border-sand-300 bg-sand-100 grid place-items-center text-stoneink-500 hover:text-terracotta-600 hover:border-terracotta-500 transition-all">
                      <div className="flex flex-col items-center text-xs">
                        <Lock size={20} />
                        <span className="mt-1">Pro</span>
                      </div>
                    </Link>
                  ) : (
                    <label className="cursor-pointer h-24 w-24 rounded-2xl border-2 border-dashed border-sand-300 hover:border-ochre-500 bg-sand-100 grid place-items-center text-stoneink-500 hover:text-terracotta-600 transition-all">
                      <input data-testid="item-photo-input" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                      <div className="flex flex-col items-center text-xs">
                        <Camera weight="duotone" size={22} />
                        <span className="mt-1">{uploading ? "…" : "Upload"}</span>
                      </div>
                    </label>
                  )}
                  {photoPath && (
                    <div className="relative">
                      <AuthedImage path={photoPath} alt="preview" className="h-24 w-24 object-cover rounded-2xl border border-sand-300" />
                      <button
                        type="button" onClick={() => setPhotoPath(null)}
                        className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-white border border-sand-300 grid place-items-center hover:bg-terracotta-50"
                        data-testid="remove-photo-btn"
                      >
                        <Trash size={14} className="text-terracotta-600" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={onClose} className="h-11 px-5 rounded-xl border border-sand-300 hover:bg-sand-200 text-stoneink-700">
                  Cancel
                </button>
                <button
                  data-testid="item-submit-btn"
                  type="submit" disabled={busy}
                  className="h-11 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium disabled:opacity-60 active:scale-[0.98] transition-all"
                >
                  {busy ? "Saving…" : initial ? "Save changes" : "Add item"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
